import type { ReactNode } from 'react'
import { cx } from './primitives'

/** A placeholder the insert menu offers, as the API describes it. */
export interface EditorPlaceholder {
  token: string
  label: string
}

interface Command {
  command: string
  value?: string
  label: string
  title: string
  className?: string
}

/**
 * The commands, grouped as the toolbar draws them.
 *
 * <p>
 * One bar for both editors, so somebody who has formatted an email knows how to format a
 * quotation's remarks. What it offers is only a promise each editor has to keep on its own:
 * an email is drawn by the customer's mail client, a narrative by QuestPDF, and a control
 * here whose formatting either of them discards is one the customer never sees the result
 * of. So nothing is added to this list without both renderers drawing it - see
 * <code>sanitizeEmailHtml</code> and <code>sanitizeRichText</code>, and RichText.ToBlocks
 * in the API.
 * </p>
 */
const GROUPS: { name: string; commands: Command[] }[] = [
  {
    name: 'history',
    commands: [
      { command: 'undo', label: '↶', title: 'Undo' },
      { command: 'redo', label: '↷', title: 'Redo' },
    ],
  },
  {
    name: 'emphasis',
    commands: [
      { command: 'bold', label: 'B', title: 'Bold', className: 'font-semibold' },
      { command: 'italic', label: 'I', title: 'Italic', className: 'italic font-serif' },
      { command: 'underline', label: 'U', title: 'Underline', className: 'underline' },
      { command: 'strikeThrough', label: 'S', title: 'Strikethrough', className: 'line-through' },
    ],
  },
  {
    name: 'alignment',
    commands: [
      { command: 'justifyLeft', label: '≡', title: 'Align left' },
      { command: 'justifyCenter', label: '≣', title: 'Centre' },
      { command: 'justifyRight', label: '≡', title: 'Align right', className: 'scale-x-[-1]' },
    ],
  },
  {
    name: 'lists',
    commands: [
      { command: 'insertUnorderedList', label: '•', title: 'Bulleted list' },
      { command: 'insertOrderedList', label: '1.', title: 'Numbered list', className: 'text-[11px]' },
      { command: 'outdent', label: '⇤', title: 'Decrease indent' },
      { command: 'indent', label: '⇥', title: 'Increase indent' },
    ],
  },
]

const BLOCKS = [
  { value: 'p', label: 'Normal' },
  { value: 'h1', label: 'Heading 1' },
  { value: 'h2', label: 'Heading 2' },
  { value: 'h3', label: 'Heading 3' },
  { value: 'blockquote', label: 'Quote' },
]

/** execCommand's own 1-7 scale, named as somebody writing would think of it. */
const SIZES = [
  { value: '2', label: 'Small' },
  { value: '3', label: 'Normal' },
  { value: '5', label: 'Large' },
  { value: '6', label: 'Huge' },
]

/**
 * The bar across the top of an editor.
 *
 * It knows the commands and nothing about the box underneath: every control hands a
 * command back, and the editor that owns the selection is the one that runs it.
 */
export function EditorToolbar({
  onCommand,
  onLink,
  placeholders = [],
  onInsert,
}: {
  /** An <code>execCommand</code> name and its value, to run against the editor's selection. */
  onCommand: (command: string, value?: string) => void
  /** Asks for an address and links the selection. The editor's, because the prompt takes focus. */
  onLink: () => void
  /** Offered from the insert menu. Empty hides the menu rather than showing an empty one. */
  placeholders?: EditorPlaceholder[]
  onInsert?: (html: string) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-1 border-b border-line-soft px-1.5 py-1">
      <ToolbarSelect
        title="Paragraph style"
        options={BLOCKS}
        onSelect={(value) => onCommand('formatBlock', `<${value}>`)}
      />

      <ToolbarSelect
        title="Text size"
        options={SIZES}
        defaultValue="3"
        onSelect={(value) => onCommand('fontSize', value)}
      />

      {GROUPS.map((group) => (
        <span key={group.name} className="flex items-center gap-0.5">
          <Divider />
          {group.commands.map((item) => (
            <ToolbarButton
              key={`${item.command}-${item.label}`}
              title={item.title}
              className={item.className}
              onClick={() => onCommand(item.command, item.value)}
            >
              {item.label}
            </ToolbarButton>
          ))}
        </span>
      ))}

      <Divider />

      <label
        title="Text colour"
        className="flex size-6 cursor-pointer items-center justify-center rounded text-[13px] text-muted hover:bg-sunken hover:text-ink"
      >
        <span aria-hidden>A</span>
        <span className="sr-only">Text colour</span>
        <input
          type="color"
          className="size-0 opacity-0"
          onMouseDown={(event) => event.stopPropagation()}
          onChange={(event) => onCommand('foreColor', event.target.value)}
        />
      </label>

      <ToolbarButton title="Link" onClick={onLink}>
        {'\u{1F517}'}
      </ToolbarButton>
      <ToolbarButton title="Remove link" onClick={() => onCommand('unlink')}>
        {'⛓'}
      </ToolbarButton>
      <ToolbarButton title="Horizontal line" onClick={() => onCommand('insertHorizontalRule')}>
        {'―'}
      </ToolbarButton>
      <ToolbarButton title="Clear formatting" onClick={() => onCommand('removeFormat')}>
        {'T✕'}
      </ToolbarButton>

      {placeholders.length > 0 && onInsert && (
        <>
          <Divider />
          <ToolbarSelect
            title="Insert a placeholder"
            label="{ }"
            options={placeholders.map((item) => ({ value: item.token, label: item.label }))}
            // Inserted as plain text so it reads as what it is. The server replaces it
            // when the mail is composed, not when the template is saved.
            onSelect={(token) => onInsert(`{{${token}}}`)}
          />
        </>
      )}
    </div>
  )
}

function Divider() {
  return <span className="mx-0.5 h-4 w-px bg-line" aria-hidden />
}

function ToolbarButton({
  title,
  className,
  onClick,
  children,
}: {
  title: string
  className?: string
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      // Kept off the button so the click never takes the selection out of the box, which is
      // what makes a toolbar apply to nothing.
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className={cx(
        'inline-flex size-6 items-center justify-center rounded text-[13px] leading-none text-muted',
        'transition-colors hover:bg-sunken hover:text-ink',
        className,
      )}
    >
      {children}
    </button>
  )
}

/**
 * A menu that acts and then forgets what was chosen.
 *
 * It never shows the current state - execCommand cannot be asked what block or size the
 * caret is in without a second source of truth that goes stale. A control that claims
 * "Normal" over a heading is worse than one that claims nothing.
 */
function ToolbarSelect({
  title,
  label,
  options,
  defaultValue,
  onSelect,
}: {
  title: string
  label?: string
  options: { value: string; label: string }[]
  defaultValue?: string
  onSelect: (value: string) => void
}) {
  return (
    <select
      title={title}
      aria-label={title}
      value=""
      onMouseDown={(event) => event.stopPropagation()}
      onChange={(event) => {
        const chosen = event.target.value
        event.target.value = ''
        if (chosen) onSelect(chosen)
      }}
      className="h-6 rounded border border-line-soft bg-surface px-1 text-[12px] text-muted hover:text-ink"
    >
      <option value="">{label ?? options.find((o) => o.value === defaultValue)?.label ?? title}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  )
}
